import React, { useCallback, useEffect, useState } from 'react';
import { cartAPI } from '../../api/cart.api';
import { orderAPI } from '../../api/order.api';
import { productAPI } from '../../api/product.api';
import CartItem from '../../components/cart/CartItem';
import CartSummary from '../../components/cart/CartSummary';
import Loader from '../../components/common/Loader';
import { useAuth } from '../../auth/AuthContext';
import { useCartWishlist } from '../../context/CartWishlistContext';
import { useLanguage } from '../../context/LanguageContext';
import {
  createDashboardOrderFromCartItems,
  createDefaultOrders,
  readUserDashboardState,
  writeUserDashboardState,
} from '../../utils/userDashboardState';
import './Cart.css';

const getProductId = (item) => item?.productId || item?.product?.id || item?.product?._id || item?.id || item?._id;

const buildLocalCart = (cartItems) => ({
  isLocalOnly: true,
  items: cartItems.map((item) => {
    const productId = getProductId(item);

    return {
      ...item,
      id: productId,
      productId,
      quantity: item.quantity || item.qty || 1,
      product: item.product || item,
    };
  }),
});

const Cart = () => {
  const { user } = useAuth();
  const { clearCart, cartItems, updateCartItem, removeFromCart } = useCartWishlist();
  const { t } = useLanguage();
  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutSuccess, setCheckoutSuccess] = useState(null);

  const fetchCart = useCallback(async () => {
    const getLocalCartProduct = (productId) =>
      cartItems.find((item) => String(getProductId(item)) === String(productId));

    if (!user?.id) {
      setCart(buildLocalCart(cartItems));
      setLoading(false);
      return;
    }

    try {
      const data = await cartAPI.getCart();
      const cartData = data?.cart || data?.item || data;

      if ((!cartData?.items || cartData.items.length === 0) && cartItems.length > 0) {
        setCart(buildLocalCart(cartItems));
        return;
      }

      if (cartData?.items && cartData.items.length > 0) {
        const itemsWithProducts = await Promise.all(
          cartData.items.map(async (item) => {
            const localProduct = getLocalCartProduct(item.productId);

            try {
              const productData = await productAPI.getById(item.productId);
              const product = productData?.product || productData?.item || productData;

              return {
                ...item,
                quantity: item.qty || item.quantity || 1,
                product: product || localProduct || item.product || null,
              };
            } catch (error) {
              console.error(`Error fetching product ${item.productId}:`, error);
              return {
                ...item,
                quantity: item.qty || item.quantity || 1,
                product: localProduct || item.product || null,
              };
            }
          })
        );
        setCart({ ...cartData, isLocalOnly: false, items: itemsWithProducts });
        return;
      }

      setCart({ ...cartData, isLocalOnly: false, items: [] });
    } catch (error) {
      console.error('Error fetching cart:', error);
      setCart(buildLocalCart(cartItems));
    } finally {
      setLoading(false);
    }
  }, [cartItems, user?.id]);

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  const handleUpdateQuantity = async (itemId, quantity) => {
    if (cart?.isLocalOnly || !user?.id) {
      await updateCartItem(itemId, quantity);
      return;
    }

    try {
      await cartAPI.updateItem(itemId, { qty: quantity });
      fetchCart();
    } catch (error) {
      console.error('Error updating item:', error);
    }
  };

  const handleRemoveItem = async (itemId) => {
    if (cart?.isLocalOnly || !user?.id) {
      await removeFromCart(itemId);
      return;
    }

    try {
      await cartAPI.removeItem(itemId);
      fetchCart();
    } catch (error) {
      console.error('Error removing item:', error);
    }
  };

  const handleCheckout = async ({ customer, paymentMethod, shippingFee, acceptedTerms, knifeVerification, card }) => {
    const items = cart?.items || [];

    if (items.length === 0) {
      throw new Error(t('cart.empty'));
    }

    const orderPayload = {
      customer,
      paymentMethod,
      shippingFee: Number(shippingFee || 0),
      acceptedTerms: Boolean(acceptedTerms),
      knifeVerification,
      card,
      items: items.map((item) => ({
        productId: getProductId(item),
        qty: item.quantity || item.qty || 1,
        prescription: item.selectedPrescription || item.prescription || item.product?.selectedPrescription || item.product?.prescription,
        specialCode: item.specialCode || item.product?.specialCode || item.productCode || item.product?.productCode || item.code || item.product?.code,
      })),
    };

    try {
      const data = await orderAPI.create(orderPayload);

      if (data?.paymentUrl) {
        window.location.assign(data.paymentUrl);
        return;
      }

      const createdOrder = data?.order || data;

      if (user?.id) {
        const order = createDashboardOrderFromCartItems(items);
        const existingOrders = readUserDashboardState('orders', user.id, createDefaultOrders());
        writeUserDashboardState('orders', user.id, [
          {
            ...order,
            id: createdOrder?.id || order.id,
            status: 'ordered',
          },
          ...existingOrders,
        ]);
      }

      if (!cart?.isLocalOnly && user?.id) {
        await Promise.all(items.map((item) => cartAPI.removeItem(item.id)));
      }

      clearCart();
      setCart((currentCart) => currentCart ? { ...currentCart, items: [] } : currentCart);
      setCheckoutSuccess(createdOrder || { paymentMethod });
    } catch (error) {
      console.error('Error creating order:', error);
      throw new Error(error.response?.data?.message || t('checkout.failed'));
    }
  };

  if (loading) return <Loader />;

  const items = cart?.items || [];

  return (
    <div className="cart-page">
      <h1>{t('cart.shoppingCart')}</h1>

      {checkoutSuccess && (
        <div className="cart-page__success">
          <strong>{t('checkout.successStrong')}</strong> {t('checkout.successText')}
        </div>
      )}

      {items.length === 0 ? (
        <p className="cart-page__empty">{t('cart.empty')}</p>
      ) : (
        <div className="cart-page__layout">
          <div className="cart-page__items">
            {items.map((item) => (
              <CartItem
                key={item.id}
                item={item}
                onUpdateQuantity={handleUpdateQuantity}
                onRemove={handleRemoveItem}
              />
            ))}
          </div>
          <CartSummary cart={cart} onCheckout={handleCheckout} />
        </div>
      )}
    </div>
  );
};

export default Cart;
